import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	AlertColor,
	Box,
	Dialog,
	Snackbar,
	Stack,
	Switch,
	Typography
} from "@mui/material";
import Api from "api";
import type { DataCreateDomain, ResGetDomains } from "api/Domains";

import AlertStyled from "components/AlertStyled";
import CustomInput from "components/CustomInput";
import TooltipStyled from "components/TooltipStyled";
import { TransitionSlideLeft } from "components/Transition";
import CustomButton from "components/customButton";

import { ReactNode, useState, Dispatch, SetStateAction } from "react";
import { Controller, useForm } from "react-hook-form";

// ВАЖНО: вместо getCreateCoupon импортируйте getCreateDomain
// и вместо полей name/id/percentOff используйте domain/maxUsers/isActive
import { getCreateDomain, useSchema } from "validation/yupValidation";

export type CreateDomainFormData = {
	domain: string;
	maxUsers: number;
	isActive: boolean;
};

interface CreateDomainsProps {
	domains: ResGetDomains[];
	setDomains: Dispatch<SetStateAction<ResGetDomains[]>>;
}

function CreateDomain({ domains, setDomains }: CreateDomainsProps)
{
	const [ isLoadingCreate, setIsLoadingCreate ] = useState<boolean>(false);
	const [ open, setOpen ] = useState<boolean>(false);

	// Вместо uniqueIdErr назовём uniqueDomainErr — логика та же.
	const [ uniqueDomainErr, setUniqueDomainErr ] = useState<boolean>(false);

	const [ snackbarContent, setSnackbarContent ] = useState<null | {
		severity: AlertColor;
		content: ReactNode;
	}>(null);

	const { control, handleSubmit, setValue } = useForm({
		resolver: useSchema(getCreateDomain),
		defaultValues: {
			domain: "",
			maxUsers: 10,
			isActive: true
		}
	});

	const onSubmit = handleSubmit(async (values) =>
	{
		const data = values as DataCreateDomain;

		if (domains.some((d) => d.domain === data.domain))
		{
			setUniqueDomainErr(true);

			return;
		}

		console.log("data ===== ", data);
		setIsLoadingCreate(true);
		Api.domains
			.createDomain(data.domain, data.maxUsers, data.isActive)
			.then(() =>
			{
				setSnackbarContent({ severity: "success", content: "Success" });
				const newDomains = [ ...domains ];

				console.log("newDomains ===", newDomains);

				newDomains.push(data as ResGetDomains);
				setUniqueDomainErr(false);
				setDomains(newDomains);
				setOpen(false);
			})
			.catch((err) =>
			{
				setSnackbarContent({ severity: "error", content: "An error occurred" });
				console.error(err);
			})
			.finally(() => setIsLoadingCreate(false));
	});

	return (
		<Box>
			<CustomButton.Base
				onClick={() => setOpen(true)}
				sx={{ width: "100%", marginTop: "24px" }}
			>
				Create a new domain
			</CustomButton.Base>

			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				open={snackbarContent !== null}
				autoHideDuration={3000}
				onClose={() => setSnackbarContent(null)}
				TransitionComponent={TransitionSlideLeft}
			>
				<AlertStyled
					onClose={() => setSnackbarContent(null)}
					severity={snackbarContent?.severity}
				>
					{snackbarContent?.content}
				</AlertStyled>
			</Snackbar>

			{/* Диалог */}
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				sx={{
					"& .MuiDialog-paper": {
						minHeight: { sm: "425px", xs: "330px" },
						p: "0 24px 24px",
						background:
							"linear-gradient(to right bottom, rgba(152, 176, 181,1 ), rgba(148, 230, 247,0.80196078431372549 ))",

						"& *:not(img)": {
							zIndex: 1
						}
					}
				}}
			>
				<Box
					sx={{
						marginTop: "24px",
						width: { sm: "568px", xs: "224px" },
						font: { sm: "400 22px/30px 'Open Sans'", xs: "400 16px/20px 'Open Sans'" },
						color: "primary.dark"
					}}
				>
					<Typography
						sx={{
							textAlign: "center",
							color: "#215C75",
							font: { lg: "700 18px/16px 'Lora-Bold'", xl: "700 22px/30px 'Lora-Bold'" },
							mt: 0
						}}
					>
						Create Domain
					</Typography>

					{/* Поле: domain */}
					<Stack gap={1} mt={2}>
						<Stack direction="row" gap={1} alignItems="center">
							<Typography
								sx={{
									color: "#000",
									font: { lg: "400 18px/16px 'Lora'", xl: "400 22px/18px 'Lora'" },
									mt: 0
								}}
							>
								Name domain
							</Typography>
							<TooltipStyled title="Enter the domain name without the '@' symbol" placement="top" arrow>
								<InfoOutlinedIcon sx={{ color: "#000" }} />
							</TooltipStyled>
						</Stack>
						<Controller
							control={control}
							name="domain"
							render={({ field, fieldState }) => (
								<>
									<CustomInput.Base
										fullWidth
										required
										{...field}
										error={Boolean(fieldState.error)}
										placeholder="domain.com"
									/>
									<Box
										sx={{
											paddingLeft: "5px",
											color: "#DC4242",
											font: "400 14px/19px 'Open Sans'"
										}}
									>
										{fieldState.error?.message}
									</Box>
								</>
							)}
						/>
						{uniqueDomainErr === true && (
							<Typography sx={{ color: "red", mt: 1 }}>
								This domain already exists
							</Typography>
						)}
					</Stack>

					{/* Поле: maxUsers */}
					<Stack gap={1} mt={2}>
						<Stack direction="row" gap={1} alignItems="center">
							<Typography
								sx={{
									color: "#000",
									font: { lg: "400 18px/16px 'Lora'", xl: "400 22px/18px 'Lora'" },
									mt: 0
								}}
							>
								Count users
							</Typography>
							<TooltipStyled
								title="Maximum number of users that can register with this domain"
								placement="top"
								arrow
							>
								<InfoOutlinedIcon sx={{ color: "#000" }} />
							</TooltipStyled>
						</Stack>
						<Controller
							control={control}
							name="maxUsers"
							render={({ field, fieldState }) => (
								<>
									<CustomInput.Base
										fullWidth
										required
										{...field}
										error={Boolean(fieldState.error)}
									/>
									<Box
										sx={{
											paddingLeft: "5px",
											color: "#DC4242",
											font: "400 14px/19px 'Open Sans'"
										}}
									>
										{fieldState.error?.message}
									</Box>
								</>
							)}
						/>
					</Stack>

					{/* Поле: isActive (Switch) */}
					<Stack gap={1} mt={2}>
						<Stack direction="row" gap={1} alignItems="center">
							<Typography
								sx={{
									color: "#000",
									font: { lg: "400 18px/16px 'Lora'", xl: "400 22px/18px 'Lora'" },
									mt: 0
								}}
							>
								Active
							</Typography>
							<TooltipStyled
								title="Toggle domain status"
								placement="top"
								arrow
							>
								<InfoOutlinedIcon sx={{ color: "#000" }} />
							</TooltipStyled>
						</Stack>
						<Controller
							control={control}
							name="isActive"
							render={({ field, fieldState }) => (
								<>
									<Stack direction="row" gap={1} alignItems="center">
										<Typography
											sx={{
												color: "#000",
												font: { lg: "400 18px/16px 'Lora'", xl: "400 22px/18px 'Lora'" },
												mt: 0
											}}
										>
											On
										</Typography>
										<Switch
											checked={field.value}
											onChange={(e) => field.onChange(e.target.checked)}
											color="primary"
											sx={{ "& .MuiSwitch-track": { cursor: "pointer" } } }
										/>
										<Typography
											sx={{
												color: "#000",
												font: { lg: "400 18px/16px 'Lora'", xl: "400 22px/18px 'Lora'" },
												mt: 0
											}}
										>
											Off
										</Typography>
									</Stack>
									<Box
										sx={{
											paddingLeft: "5px",
											color: "#DC4242",
											font: "400 14px/19px 'Open Sans'"
										}}
									>
										{fieldState.error?.message}
									</Box>
								</>
							)}
						/>
					</Stack>
				</Box>

				{/* Кнопка Save */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						marginTop: "24px",
						width: "100%"
					}}
				>
					<CustomButton.Base
						onClick={onSubmit}
						sx={{
							width: { sm: "auto", xs: "100%" }
						}}
						loading={isLoadingCreate}
					>
						Save
					</CustomButton.Base>
				</Box>
			</Dialog>
		</Box>
	);
}

export default CreateDomain;
